import { debounce } from "@mui/material";
import { useCachedFragment } from "@sinch/hooks/useCachedFragment";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";
import { mapToOptions, Option } from "@sinch/utils/option";
import { ensureArray, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useMemo } from "react";
import { CompaniesData, CompaniesListItem } from "./queries.graphql";
import { CompaniesDataQuery, CompaniesDataQueryVariables, CompaniesListItemFragment } from "./queries.types";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import type { AutocompleteInputProps } from "@sinch/components/form/AutocompleteInput/types";
import { AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

export const CompanySelect = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: Omit<AutocompleteInputProps<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>, "ref" | "options">
) => {
  const { data, dispatch, loading } = useLazyQueryHandler<CompaniesDataQuery, CompaniesDataQueryVariables>(
    CompaniesData
  );

  const debounceDispatch = useMemo(() => debounce(dispatch, 200), [dispatch]);

  const handleSearch = (search: string) => {
    // an empty search loads the first records, so focusing the input opens a dropdown with companies
    debounceDispatch({
      filter: isNotNilOrEmpty(search) ? { name: { LIKE: search } } : null,
      limit: SEARCH_FIELD_LIMIT,
    });
  };

  const { data: companies, loading: initLoading } = useCachedFragment<CompaniesListItemFragment>({
    fragment: CompaniesListItem,
    type: "Company",
    ids: props.value ? ensureArray(props.value as string[]) : [],
    query: CompaniesData,
    variables: {
      filter: props.value ? { id: { IN: ensureArray(props.value) } } : null,
    },
  });

  const companyOptions: Option[] = useMemo(() => mapToOptions("id", "name")(data?.companies || []), [data]);
  const options: Option[] = useMemo(() => {
    const valueCompanies = mapToOptions("id", "name")(companies?.filter(isNotNil) || []) as Option<string, string>[];
    return [...companyOptions, ...valueCompanies];
  }, [companyOptions, companies]);

  return (
    <AutocompleteInput<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>
      filterOptions={() => companyOptions}
      limitCount={SEARCH_FIELD_LIMIT}
      loading={loading}
      openOnFocus
      skeleton={initLoading}
      {...props}
      defaultValue={props.defaultValue ?? undefined}
      onSearch={handleSearch}
      options={options}
    />
  );
};
